import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const EXTERIOR_IMG = "https://customer-assets.emergentagent.com/job_turnkey-deploy/artifacts/owalvvre_kitchen%20ext%20-02.jpg";

const benefits = [
  "Turnkey restaurant shell delivered complete",
  "Minimal sitework required",
  "Long-term ground lease income",
  "Zero tenant buildout risk",
  "Pre-screened operators",
  "Insurance-ready documentation",
  "Modular portability — the unit can be relocated returning your property to its original state",
];

const timeline = [
  { step: "01", title: "Site Review", desc: "Zoning, access, utilities" },
  { step: "02", title: "Ground Lease Structure", desc: "Clean lease terms tailored to your site" },
  { step: "03", title: "Pad Delivery", desc: "Minimal disruption" },
  { step: "04", title: "Operator Move-In", desc: "Fast revenue" },
];

export default function LandlordsPage() {
  return (
    <div>
      {/* Hero */}
      <section
        data-testid="landlords-hero"
        className="relative overflow-hidden"
        style={{ backgroundColor: "#2d2d2d" }}
      >
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(${EXTERIOR_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(45,45,45,0.55)" }} />

        <div className="relative w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pt-28 pb-14 md:py-20">
          <div className="max-w-3xl">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-5 opacity-0 animate-fade-up"
              style={{ color: "#d4782f" }}
            >
              For Landlords
            </p>
            <h1
              className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.08] mb-5 opacity-0 animate-fade-up animate-delay-1"
              style={{ color: "#FFFFFF" }}
            >
              Transform Underutilized Land Into Predictable Income.
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed max-w-2xl opacity-0 animate-fade-up animate-delay-2"
              style={{ color: "rgba(255,255,255,0.75)" }}
            >
              We deliver a complete restaurant pad with no construction risk, no
              delays, and no capital outlay from you.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section
        data-testid="landlord-benefits"
        style={{ backgroundColor: "#F4F5F7" }}
      >
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ color: "#d4782f" }}
          >
            Landlord Advantages
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-8 md:mb-10"
            style={{ color: "#1A1D23" }}
          >
            Why Landlords Partner With Us
          </h2>

          {/* Institutional Grade Callout */}
          <div
            className="mb-10 p-8 md:p-10 border-l-4"
            style={{ borderColor: "#d4782f", backgroundColor: "rgba(212,120,47,0.06)" }}
          >
            <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-3" style={{ color: "#d4782f" }}>
              Institutional Grade
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#1A1D23" }}>
              Every PadSite deployment comes with insurance-ready infrastructure, and modular portability — protecting your investment regardless of tenant performance.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
            {benefits.map((b, i) => (
              <div
                key={i}
                data-testid={`landlord-benefit-${i}`}
                className="flex items-start gap-4"
              >
                <div
                  className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: "#d4782f" }}
                />
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: "#1A1D23" }}
                >
                  {b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Unit on Site Image */}
      <section style={{ backgroundColor: "#FFFFFF" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
            <img
              src="https://customer-assets.emergentagent.com/job_turnkey-deploy/artifacts/ipde32x3_Starbucks-container.jpg"
              alt="Starbucks container drive-thru — major brand PadSite deployment"
              className="w-full h-full object-cover"
              data-testid="landlord-unit-image"
            />
            <div
              className="absolute bottom-0 left-0 right-0 h-1"
              style={{ backgroundColor: "#d4782f" }}
            />
          </div>
        </div>
      </section>

      {/* Stacked Tower — vertical potential of a small pad */}
      <section data-testid="landlord-tower" style={{ backgroundColor: "#2d2d2d" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p
                className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
                style={{ color: "#d4782f" }}
              >
                Vertical Potential
              </p>
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] mb-6"
                style={{ color: "#FFFFFF" }}
              >
                One Pad.
                <br />
                <span style={{ color: "#d4782f" }}>Every Level Earns.</span>
              </h2>
              <p
                className="text-base md:text-lg leading-relaxed"
                style={{ color: "rgba(255,255,255,0.65)" }}
              >
                A PadSite footprint doesn't stop at ground level. Stack kitchens,
                decks, and terraces to multiply leasable space on the same parcel
                — turning a small pad into a multi-story destination.
              </p>
            </div>
            <div className="relative overflow-hidden">
              <img
                src="/images/food-court-blkbox.webp"
                alt="Three-story container food hall with stacked wooden decks and rooftop terrace built on a single pad"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="landlord-tower-image"
              />
              <div
                className="absolute bottom-0 left-0 right-0 h-1"
                style={{ backgroundColor: "#d4782f" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section
        data-testid="landlord-process"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ color: "#d4782f" }}
          >
            Our Process
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-8 md:mb-10"
            style={{ color: "#1A1D23" }}
          >
            A Clean, Predictable Deployment Model
          </h2>

          <div className="relative">
            {/* Vertical accent line */}
            <div
              className="absolute left-[15px] top-0 bottom-0 w-px hidden md:block"
              style={{ backgroundColor: "rgba(212, 120, 47, 0.3)" }}
            />

            <div className="space-y-10 md:space-y-12">
              {timeline.map((item, i) => (
                <div
                  key={i}
                  data-testid={`landlord-step-${i}`}
                  className="flex items-start gap-8 md:gap-12"
                >
                  <div className="relative flex-shrink-0">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{
                        backgroundColor: "#d4782f",
                        color: "#FFFFFF",
                      }}
                    >
                      {item.step}
                    </div>
                  </div>
                  <div>
                    <h3
                      className="text-xl md:text-2xl font-bold tracking-tight mb-2"
                      style={{ color: "#1A1D23" }}
                    >
                      {item.title}
                    </h3>
                    <p
                      className="text-sm md:text-base leading-relaxed"
                      style={{ color: "#4A4F5C" }}
                    >
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="landlord-contact-cta"
              className="inline-flex items-center gap-2 px-8 py-4 font-bold tracking-wide transition-colors rounded-none text-sm"
              style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#E08A45")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#d4782f")}
            >
              Start a Conversation
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
